import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Building2, PhoneMissed, PhoneCall, Users, Voicemail as VmIcon, LogIn, Coffee, Clock, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import * as D from "@/data/analyticsData";
import { ANALYTICS } from "@/data/pageCopy";

const { kicker, h1Lines, lead } = ANALYTICS;

const BLUE = "#0047FF";
const axis = { tick: { fill: "#a3a3a3", fontSize: 10, fontFamily: "JetBrains Mono" }, axisLine: false, tickLine: false };

const useCountUp = (target, dur = 900) => {
  const [v, setV] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    let start, raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / dur, 1);
      setV(from + (target - from) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return v;
};

const KPI = ({ label, value, suffix = "", accent, raw, live }) => {
  const n = useCountUp(typeof value === "number" ? value : 0);
  return (
    <motion.div whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300, damping: 22 }} className="bg-white/[0.03] border border-white/10 p-6 relative">
      {live && (
        <span className="absolute top-4 right-4 flex items-center gap-1.5 font-mono-t text-[9px] uppercase tracking-widest text-[#5B8CFF]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#0047FF] animate-pulse" /> live
        </span>
      )}
      <p className={`font-heading text-4xl tracking-tight ${accent ? "text-[#5B8CFF]" : "text-white"}`}>
        {raw ? value : Math.round(n)}{suffix}
      </p>
      <p className="mt-2 font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
    </motion.div>
  );
};

const Panel = ({ title, children, className = "", right }) => (
  <motion.div whileHover={{ y: -4, borderColor: "rgba(0,71,255,0.4)" }} transition={{ type: "spring", stiffness: 300, damping: 24 }} className={`bg-white/[0.03] border border-white/10 p-6 ${className}`}>
    <div className="flex items-center justify-between mb-5">
      <p className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">{title}</p>
      {right}
    </div>
    {children}
  </motion.div>
);

const statusPill = (s) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-t uppercase tracking-wider ${s === "Missed" ? "bg-neutral-900 text-white" : "bg-[#0047FF] text-white"}`}>{s}</span>
);

// live-feel CDR feed
const CdrFeed = () => {
  const [count, setCount] = useState(3);
  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c >= D.cdrs.length ? 3 : c + 1)), 1600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="space-y-1.5 min-h-[280px]">
      <AnimatePresence initial={false}>
        {D.cdrs.slice(0, count).map((r, i) => (
          <motion.div
            key={`${count}-${i}`}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-white/5 py-2.5 text-sm"
          >
            <span className="font-mono-t text-[12px] text-neutral-400">{r.time}</span>
            <span className="font-mono-t text-[12px] text-neutral-300 truncate">{r.from} <span className="text-neutral-300">→</span> {r.to}</span>
            <span className="flex items-center gap-3">
              <span className="font-mono-t text-[12px] text-neutral-500">{r.dur}</span>
              {statusPill(r.status)}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

const moreReports = [
  { icon: Building2, t: "By Tenant", d: "Per-tenant call volume & answer rates" },
  { icon: PhoneMissed, t: "Missed Calls", d: "Who called, which DID, how long they waited" },
  { icon: PhoneCall, t: "DID Traffic", d: "Inbound vs outbound per number" },
  { icon: Users, t: "Queue", d: "Waiting, avg wait & SLA per queue" },
  { icon: VmIcon, t: "Voicemail", d: "Messages by caller and mailbox" },
  { icon: LogIn, t: "Login / Logout", d: "Agent session & online time" },
  { icon: Coffee, t: "Break Time", d: "Breaks taken and duration per agent" },
  { icon: Clock, t: "Call Duration", d: "Distribution across call lengths" },
];

export default function PbxAnalyticsPage() {
  const [live, setLive] = useState({ total: D.kpis.totalCalls, rate: D.kpis.answerRate, missed: D.kpis.missed });
  useEffect(() => {
    const id = setInterval(() => {
      setLive({
        total: 1270 + Math.floor(Math.random() * 44),
        rate: 86 + Math.floor(Math.random() * 5),
        missed: 148 + Math.floor(Math.random() * 12),
      });
    }, 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="pbx-analytics-page">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Header */}
        <div className="relative">
          <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "64px 64px" }} />
          <div className="relative">
            <Reveal><p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">{kicker}</p></Reveal>
            <h1 className="font-heading font-medium tracking-tight leading-[0.98] text-4xl md:text-6xl text-white max-w-3xl">
              <MaskedLines lines={h1Lines} delay={0.1} />
            </h1>
            <Reveal delay={0.35}>
              <p className="mt-7 text-lg text-neutral-400 max-w-xl leading-relaxed">
                {lead}
              </p>
            </Reveal>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Reveal><KPI label="Total calls today" value={live.total} live /></Reveal>
          <Reveal delay={0.05}><KPI label="Answer rate" value={live.rate} suffix="%" accent live /></Reveal>
          <Reveal delay={0.1}><KPI label="Avg talk time" value={D.kpis.avgTalk} raw /></Reveal>
          <Reveal delay={0.15}><KPI label="Missed" value={live.missed} live /></Reveal>
        </div>

        {/* Charts */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <Reveal>
            <Panel title="Calls by hour">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={D.callsByHour} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="h" {...axis} interval={2} /><YAxis {...axis} />
                  <Bar dataKey="v" fill={BLUE} radius={[2, 2, 0, 0]} animationDuration={900} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </Reveal>
          <Reveal delay={0.1}>
            <Panel title="Daily volume (last 30 days)">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={D.daily30} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                  <defs><linearGradient id="dv2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={BLUE} stopOpacity={0.3} /><stop offset="100%" stopColor={BLUE} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="d" {...axis} interval={4} /><YAxis {...axis} />
                  <Area type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2} fill="url(#dv2)" animationDuration={1000} />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>
          </Reveal>
        </div>

        {/* Heatmap */}
        <div className="mt-4">
          <Reveal>
            <Panel title="Hourly heatmap · calls by day & hour">
              <div className="overflow-x-auto">
                <div className="min-w-[680px] relative">
                  <motion.div
                    className="absolute top-0 bottom-0 w-16 pointer-events-none z-10"
                    style={{ background: "linear-gradient(90deg, transparent, rgba(0,71,255,0.12), transparent)" }}
                    animate={{ left: ["6%", "100%"] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <div className="flex gap-1 mb-1 pl-10">
                    {Array.from({ length: 24 }).map((_, h) => <span key={h} className="flex-1 text-center font-mono-t text-[8px] text-neutral-400">{h}</span>)}
                  </div>
                  {D.heatmap.map((row, i) => (
                    <div key={i} className="flex gap-1 items-center mb-1">
                      <span className="w-9 font-mono-t text-[10px] text-neutral-500">{D.heatDays[i]}</span>
                      {row.map((v, h) => (
                        <motion.span key={h} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: (i * 24 + h) * 0.002 }} className="flex-1 h-6" style={{ background: `rgba(0,71,255,${Math.max(0.05, v / 100)})` }} title={`${v} calls`} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </Reveal>
        </div>

        {/* Team status / wallboard */}
        <div className="mt-4">
          <Reveal>
            <Panel
              title="Team status · 2 on call · 3 available"
              right={<span className="font-mono-t text-[10px] uppercase tracking-widest text-[#5B8CFF]">Wallboard →</span>}
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {D.agents.slice(0, 5).map((a, i) => {
                  const status = i === 0 || i === 2 ? "On call" : i === 4 ? "Away" : "Available";
                  return (
                    <div key={a.name} className="flex items-center gap-2.5 text-sm">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${status === "On call" ? "bg-[#5B8CFF] animate-pulse" : status === "Available" ? "bg-emerald-400" : "bg-neutral-600"}`} />
                      <div className="min-w-0">
                        <p className="text-neutral-200 truncate">{a.name}</p>
                        <p className="font-mono-t text-[10px] text-neutral-500">{status}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </Reveal>
        </div>

        {/* Agent performance + CDR feed */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <Reveal>
            <Panel title="Agent performance">
              <div className="space-y-4">
                {D.agents.slice(0, 5).map((a, i) => (
                  <div key={a.name} className="flex items-center gap-4">
                    <span className="grid place-items-center w-8 h-8 bg-neutral-900 text-white font-mono-t text-[11px] shrink-0">{a.ext}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-200 truncate">{a.name}</span>
                        <span className="font-mono-t text-neutral-400 text-xs">{a.answered}/{a.calls} · {a.avgTalk}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-white/10">
                        <motion.div className="h-full bg-[#0047FF]" initial={{ width: 0 }} whileInView={{ width: `${a.rating}%` }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.08 }} />
                      </div>
                    </div>
                    <span className="font-mono-t text-sm text-white w-10 text-right">{a.rating}%</span>
                  </div>
                ))}
              </div>
            </Panel>
          </Reveal>
          <Reveal delay={0.1}>
            <Panel title="Call detail records (CDRs)" right={<span className="flex items-center gap-1.5 font-mono-t text-[10px] uppercase tracking-widest text-[#5B8CFF]"><span className="w-1.5 h-1.5 rounded-full bg-[#0047FF] animate-pulse" /> Live</span>}>
              <CdrFeed />
            </Panel>
          </Reveal>
        </div>

        {/* And much more */}
        <div className="mt-16">
          <Reveal>
            <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white">Plus a report for everything else</h2>
            <p className="mt-3 text-neutral-500 max-w-lg">Every tenant also gets these in the platform — filterable by date range and tenant, with one-click CSV export.</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {moreReports.map((m, i) => (
              <Reveal key={m.t} delay={(i % 4) * 0.05}>
                <div className="h-full bg-white/[0.02] hover:bg-white/[0.05] transition-colors group min-h-[150px]">
                  <m.icon className="text-[#5B8CFF] group-hover:scale-110 transition-transform origin-left" size={22} />
                  <h3 className="mt-4 font-heading font-semibold text-base text-white">{m.t}</h3>
                  <p className="mt-1.5 text-neutral-500 text-xs leading-relaxed">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#0a0a0a] text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-heading font-medium tracking-tight text-2xl md:text-4xl max-w-xl">See these reports on your own numbers.</h2>
          <Link to="/demo" className="group inline-flex items-center gap-2 bg-white text-black font-bold px-7 py-4 hover:bg-neutral-200 transition-colors shrink-0" data-testid="analytics-cta">
            Book a demo <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}
